import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { isVideo } from '@application/helpers/video-detection-helper';
import { Avatar, Box, ClickAwayListener, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import EventPreviewImg from '@public/assets/images/event-preview.png';
import { useResponsive } from '@ui/style/useResponsive';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useHomeEventsFrameStyles from './useHomeEventsFrameStyles';
import { ArrowRight } from 'iconsax-react';
const HomeEventFrame = ({ registrationsStarted = true, event }) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const styles = useHomeEventsFrameStyles();
    const { isMobile } = useResponsive();
    const [open, setOpen] = useState(false);
    const tooltipRef = useRef(null);
    const timeoutRef = useRef(null);
    useEffect(() => {
        if (!isMobile) {
            // Add scroll listener for desktop to close tooltip
            const handleScroll = (e) => {
                if (e.target === document || e.target === window) {
                    setOpen(false);
                }
            };
            // Add wheel event listener for better scroll detection
            const handleWheel = () => {
                if (open) {
                    setOpen(false);
                }
            };
            window.addEventListener('scroll', handleScroll, { passive: true, capture: true });
            window.addEventListener('wheel', handleWheel, { passive: true });
            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('wheel', handleWheel);
            };
        }
        if (!isMobile)
            return;
        const handleScroll = (e) => {
            if (e.target === document || e.target === window) {
                setOpen(false);
            }
        };
        let touchStartX = 0;
        let touchStartY = 0;
        const handleTouchStart = (e) => {
            touchStartX = e.touches[0].clientX;
            touchStartY = e.touches[0].clientY;
        };
        const handleTouchMove = (e) => {
            const diffX = Math.abs(e.touches[0].clientX - touchStartX);
            const diffY = Math.abs(e.touches[0].clientY - touchStartY);
            if (diffX > 10 || diffY > 10) {
                setOpen(false);
            }
        };
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('touchstart', handleTouchStart, { passive: true });
        window.addEventListener('touchmove', handleTouchMove, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('touchstart', handleTouchStart);
            window.removeEventListener('touchmove', handleTouchMove);
        };
    }, [isMobile, open]);
    // Clear timeout on unmount
    const handleMouseEnter = () => {
        if (!isMobile) {
            // Clear any existing timeout
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setOpen(true);
        }
    };
    const handleMouseLeave = () => {
        if (!isMobile) {
            // Add a small delay to prevent flickering and ensure proper cleanup
            timeoutRef.current = setTimeout(() => {
                setOpen(false);
            }, 100);
        }
    };
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };
    const getCompanyNames = () => {
        if (!event?.eventCompanies || event.eventCompanies.length === 0)
            return 'N/A';
        if (event.eventCompanies.length === 1)
            return event.eventCompanies[0].label;
        return `${event.eventCompanies[0].label} +${event.eventCompanies.length - 1}`;
    };
    const getAllCompanyNames = () => {
        if (!event?.eventCompanies || event.eventCompanies.length === 0)
            return 'N/A';
        return event.eventCompanies.map((company) => company.label).join(', ');
    };
    const getResponsesText = () => {
        if (!event?.responsesCount || event.responsesCount === 0)
            return 0;
        return event.responsesCount;
    };
    const getBackgroundImage = () => {
        if (!event?.uploadedImages)
            return EventPreviewImg;
        const backgroundImage = event.uploadedImages.find((img) => img.isBackgroundImage);
        if (!backgroundImage?.url)
            return EventPreviewImg;
        return `${backgroundImage.url}`;
    };
    const getAssignedUsers = () => {
        return event?.assignedUsers || [];
    };
    const getUserInitials = (user) => {
        const firstName = user?.firstName || '';
        const lastName = user?.lastName || '';
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };
    const handleCardClick = () => {
        if (event?.id) {
            navigate(`/event-details/${event.id}`);
        }
    };
    return (_jsx(Box, { className: 'event_frame', sx: {
            ...styles.eventFrame
        }, children: _jsxs(Box, { className: 'box_top', sx: {
                display: 'flex',
                gap: 1.5,
                alignItems: 'center'
            }, children: [event?.uploadedImages?.find((img) => img.isBackgroundImage)?.url ? (_jsx(Box, { component: isVideo(undefined, getBackgroundImage()) ? 'video' : 'img', src: getBackgroundImage(), autoPlay: isVideo(undefined, getBackgroundImage()), muted: isVideo(undefined, getBackgroundImage()), loop: isVideo(undefined, getBackgroundImage()), playsInline: isVideo(undefined, getBackgroundImage()), sx: {
                        ...styles.eventPreviewImage,
                        objectFit: 'cover'
                    } })) : null, _jsxs(Box, { className: 'content__container', sx: {
                        ...styles.contentContainer
                    }, children: [_jsx(Typography, { variant: 'subtitle2', fontWeight: 500, lineHeight: '100%', color: theme.palette.grey[900], style: { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }, children: event?.title }), _jsxs(Box, { sx: {
                                mt: theme.spacing(1),
                                display: 'flex',
                                flexDirection: 'column',
                                gap: theme.spacing(0.5)
                            }, children: [_jsx(Typography, { variant: 'overline', children: event && `${formatDate(event.startDate)} → ${formatDate(event.endDate)}` }), _jsx(ClickAwayListener, { onClickAway: () => setOpen(false), children: _jsx("div", { ref: tooltipRef, style: {
                                            display: 'flex',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis',
                                            overflow: 'hidden'
                                        }, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: _jsx(Tooltip, { placement: 'bottom-start', title: _jsx(Box, { onClick: (e) => e.stopPropagation(), sx: { maxWidth: 300, maxHeight: 150, overflow: 'auto' }, onMouseEnter: () => {
                                                    if (!isMobile) {
                                                        if (timeoutRef.current) {
                                                            clearTimeout(timeoutRef.current);
                                                        }
                                                        setOpen(true);
                                                    }
                                                }, onMouseLeave: handleMouseLeave, onScroll: (e) => e.stopPropagation(), onWheel: (e) => e.stopPropagation(), children: getAllCompanyNames() }), open: open, disableHoverListener: isMobile, disableFocusListener: isMobile, disableTouchListener: isMobile, children: _jsx(Typography, { variant: 'caption', fontWeight: 400, color: theme.palette.grey[700], sx: { cursor: 'pointer' }, style: { whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }, onClick: (e) => {
                                                    e.stopPropagation();
                                                    if (isMobile) {
                                                        setOpen((prev) => !prev);
                                                    }
                                                }, children: event && getCompanyNames() }) }) }) })] })] }), _jsx(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0
                    }, children: getAssignedUsers() && getAssignedUsers().length > 1 ? (_jsxs(Box, { sx: {
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            minWidth: getAssignedUsers().length > 3
                                ? 72 + String(getAssignedUsers().length - 3).length * 10
                                : 72,
                            pr: getAssignedUsers().length > 3 ? 1 : 0,
                            overflow: 'visible'
                        }, children: [getAssignedUsers()
                                .slice(0, 3)
                                .map((user, idx) => (_jsx(Avatar, { variant: 'rounded', src: user?.profilePictureUrl &&
                                    user?.profilePictureUrl !== '/placeholder.svg?height=24&width=24'
                                    ? user?.profilePictureUrl
                                    : undefined, alt: `${user?.firstName || ''} ${user?.lastName || ''}`, sx: {
                                    width: 32,
                                    height: 32,
                                    border: '2px solid #fff',
                                    fontSize: '14px',
                                    fontWeight: 400,
                                    ml: idx === 0 ? 0 : '-8px',
                                    zIndex: 3 - idx,
                                    backgroundColor: theme.palette.background.neutral,
                                    color: theme.palette.action.active,
                                    borderRadius: '12px'
                                }, children: !user?.profilePictureUrl ||
                                    user?.profilePictureUrl === '/placeholder.svg?height=24&width=24' ? (getUserInitials(user)) : (_jsx(_Fragment, {})) }, idx))), getAssignedUsers().length > 3 && (_jsx(Avatar, { variant: 'rounded', sx: {
                                    width: 40,
                                    height: 32,
                                    border: '2px solid #fff',
                                    fontSize: '12px',
                                    fontWeight: 500,
                                    backgroundColor: theme.palette.primary.lighter,
                                    color: theme.palette.primary.dark,
                                    ml: '-8px',
                                    zIndex: 0,
                                    px: 1.5,
                                    overflow: 'visible',
                                    minWidth: 40,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box'
                                }, children: `+${getAssignedUsers().length - 3}` }))] })) : getAssignedUsers().length === 1 ? (_jsx(Avatar, { variant: 'rounded', sx: {
                            width: 32,
                            height: 32,
                            border: '2px solid #fff',
                            fontSize: '14px',
                            fontWeight: 400,
                            backgroundColor: theme.palette.background.neutral,
                            color: theme.palette.action.active,
                            borderRadius: '12px'
                        }, src: getAssignedUsers()[0]?.profilePictureUrl &&
                            getAssignedUsers()[0]?.profilePictureUrl !== '/placeholder.svg?height=24&width=24'
                            ? getAssignedUsers()[0]?.profilePictureUrl
                            : undefined, children: getUserInitials(getAssignedUsers()[0]) })) : null }), _jsx(IconButton, { size: 'small', color: 'primary', onClick: handleCardClick, sx: {
                        width: 36,
                        height: 36,
                        borderRadius: '12px',
                        backgroundColor: theme.palette.text.primary,
                        color: theme.palette.common.white,
                        '&:hover': {
                            backgroundColor: theme.palette.text.primary
                        },
                        '&:disabled': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled
                        }
                    }, children: _jsx(ArrowRight, { width: 20, height: 20 }) })] }) }));
};
export default HomeEventFrame;
